<template>
  <div class="lorentz-chamber-viz">
    
    <div class="glass-header">
      <div class="title-group">
         <span class="main-title">洛伦兹磁约束模拟器</span>
         <span class="sub-title">Magnetic Confinement & Lorentz Force</span>
      </div>
      <div class="formula-display">F = q(v × B)</div>
    </div>

    <div class="viz-main">
      <!-- 渲染区 -->
      <div ref="canvasContainer" class="rendering-chamber">
          <!-- 独特 HUD -->
          <div class="sci-fi-hud">
             <div class="hud-item ring">
                <svg width="60" height="60" viewBox="0 0 60 60">
                   <circle cx="30" cy="30" r="25" fill="none" stroke="rgba(0, 210, 255, 0.2)" stroke-width="2" />
                   <circle cx="30" cy="30" r="25" fill="none" stroke="#00d2ff" stroke-width="2" stroke-dasharray="157" :stroke-dashoffset="157 * (1 - magneticField/2)" transform="rotate(-90 30 30)" />
                </svg>
                <div class="label">B-FIELD</div>
                <div class="val">{{ magneticField.toFixed(1) }}T</div>
             </div>
             <div class="hud-item stats">
                <div class="s-row"><span class="l">粒子速度:</span> <span class="v">{{ velocity }} m/s</span></div>
                <div class="s-row"><span class="l">运动特征:</span> <span class="v highlight">匀速圆周</span></div>
             </div>
          </div>
      </div>

      <!-- 参数控制区 -->
      <div class="tech-panel">
         <div class="control-section">
            <div class="slider-box">
               <div class="tag">速度分量 (v)</div>
               <el-slider v-model="velocity" :min="10" :max="100" size="mini"></el-slider>
            </div>
            <div class="slider-box">
               <div class="tag">磁通量 (B)</div>
               <el-slider v-model="magneticField" :min="0.1" :max="2.0" :step="0.1" size="mini"></el-slider>
            </div>
         </div>

         <div class="discovery-log">
            <i class="el-icon-cpu"></i>
            <div class="msg">
               <strong>观测笔记：</strong>洛伦兹力始终与运动方向垂直，因此不对粒子做功。观察半径 $R = mv/qB$，当磁场减弱时，受到的约束力减小，粒子轨迹半径显著增大。
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'LorentzForceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      velocity: 60,
      magneticField: 0.8,
      running: true,
      time: 0,
      renderer: null,
      scene: null,
      camera: null,
      particle: null,
      trail: null,
      trailPoints: [],
      canvasContainer: null
    }
  },
  mounted() { 
    this.$nextTick(() => { this.tryInitThree(); });
  },
  beforeDestroy() { 
    this.running = false;
    if (this.initTimer) clearTimeout(this.initTimer);
    if (this.renderer) {
      if (this.canvasContainer && this.renderer.domElement) {
        if (this.canvasContainer.contains(this.renderer.domElement)) {
          this.canvasContainer.removeChild(this.renderer.domElement);
        }
      }
      this.renderer.dispose();
    }
  },
  methods: {
    tryInitThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      if (!this.canvasContainer) return;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;
      if (width === 0 || height === 0) {
        this.initTimer = setTimeout(this.tryInitThree, 200);
        return;
      }
      this.initThree();
    },
    initThree() {
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      // 深邃极光背景
      this.scene.background = new THREE.Color(0x02040a);
      this.scene.fog = new THREE.Fog(0x02040a, 50, 200);

      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(50, 50, 80);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // 磁场蓝光线
      const arrowGroup = new THREE.Group();
      for(let i=-3; i<=3; i++) {
        for(let j=-3; j<=3; j++) {
           const arrow = new THREE.ArrowHelper(
             new THREE.Vector3(0,-1,0).normalize(), 
             new THREE.Vector3(i*20, 50, j*20), 
             15, 0x00d2ff, 4, 1.5
           );
           arrow.line.material.transparent = true;
           arrow.line.material.opacity = 0.2;
           arrowGroup.add(arrow);
        }
      }
      this.scene.add(arrowGroup);

      // 核心粒子 - 带有自发光
      this.particle = new THREE.Mesh(
        new THREE.SphereGeometry(3, 32, 32), 
        new THREE.MeshPhongMaterial({ color: 0xff4757, emissive: 0xff4757, emissiveIntensity: 2 })
      );
      this.scene.add(this.particle);

      // 粒子轨迹 (Trail)
      const trailGeo = new THREE.BufferGeometry();
      this.trailMat = new THREE.LineBasicMaterial({ color: 0xff4757, transparent: true, opacity: 0.6 });
      this.trail = new THREE.Line(trailGeo, this.trailMat);
      this.scene.add(this.trail);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.3));
      const pointLight = new THREE.PointLight(0x00d2ff, 1, 200);
      pointLight.position.set(0, 50, 0);
      this.scene.add(pointLight);

      this.animate();
    },
    animate() {
      if (!this.running || !this.renderer) return;
      requestAnimationFrame(this.animate);
      
      this.time += 0.02;
      const r = (this.velocity) / (this.magneticField * 4); // 视觉比例调整
      const omega = this.velocity / r / 50;
      
      const px = r * Math.cos(this.time * omega);
      const pz = r * Math.sin(this.time * omega);
      
      this.particle.position.set(px, 0, pz);
      
      // 更新轨迹
      this.trailPoints.push(new THREE.Vector3(px, 0, pz));
      if (this.trailPoints.length > 100) this.trailPoints.shift();
      this.trail.geometry.setFromPoints(this.trailPoints);

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.lorentz-chamber-viz {
  flex: 1; display: flex; flex-direction: column; height: 100%; min-height: 500px;
  background: linear-gradient(135deg, #050a14 0%, #02040a 100%);
  color: #fff; font-family: 'Segoe UI', Roboto, sans-serif;
}

.glass-header {
  padding: 15px 25px; display: flex; justify-content: space-between; align-items: flex-end;
  background: rgba(255,255,255,0.02); border-bottom: 1px solid rgba(255,255,255,0.05);
  .title-group {
    .main-title { display: block; font-size: 1.2rem; font-weight: 800; color: #00d2ff; text-transform: uppercase; letter-spacing: 2px; }
    .sub-title { font-size: 0.7rem; color: #57606f; font-weight: 500; }
  }
  .formula-display {
    font-size: 1.1rem; font-family: 'Times New Roman', serif; font-style: italic; color: #ff4757; font-weight: bold;
    text-shadow: 0 0 10px rgba(255, 71, 87, 0.4);
  }
}

.viz-main { flex: 1; display: flex; flex-direction: column; min-height: 0; padding: 15px; gap: 15px; }

.rendering-chamber {
  flex: 1; min-height: 320px; border-radius: 16px; position: relative; overflow: hidden;
  border: 1px solid rgba(0, 210, 255, 0.2); box-shadow: inset 0 0 30px rgba(0, 210, 255, 0.1);
  
  .sci-fi-hud {
    position: absolute; top: 15px; left: 15px; z-index: 10; display: flex; gap: 20px; pointer-events: none;
    .hud-item {
      background: rgba(10, 15, 30, 0.7); backdrop-filter: blur(8px); border-radius: 10px; border: 1px solid rgba(255,255,255,0.1);
      &.ring {
        padding: 5px 12px; display: flex; flex-direction: column; align-items: center;
        .label { font-size: 0.5rem; color: #00d2ff; opacity: 0.7; }
        .val { font-size: 1rem; color: #fff; font-weight: bold; font-family: monospace; }
      }
      &.stats {
        padding: 10px 15px; display: flex; flex-direction: column; gap: 4px;
        .s-row {
          display: flex; gap: 10px; font-size: 0.7rem;
          .l { color: #888; } .v { color: #fff; font-weight: bold; }
          .highlight { color: #2ed573; text-shadow: 0 0 6px rgba(46, 213, 115, 0.5); }
        }
      }
    }
  }
}

.tech-panel {
  display: flex; gap: 20px;
  .control-section {
    flex: 1.2; display: grid; grid-template-columns: 1fr 1fr; gap: 15px;
    background: rgba(255,255,255,0.03); padding: 15px; border-radius: 12px;
    .slider-box {
       .tag { font-size: 0.7rem; color: #57606f; font-weight: bold; margin-bottom: 5px; }
    }
  }
  .discovery-log {
    flex: 1; background: rgba(0, 210, 255, 0.05); border: 1px solid rgba(0, 210, 255, 0.1); border-radius: 12px;
    padding: 12px 18px; display: flex; gap: 15px; align-items: flex-start;
    i { color: #00d2ff; font-size: 1.4rem; padding-top: 2px; }
    .msg { 
      font-size: 0.75rem; color: #a2a2a2; line-height: 1.6;
      strong { color: #00d2ff; display: block; margin-bottom: 3px; }
    }
  }
}

::v-deep .el-slider__runway { background-color: rgba(255,255,255,0.05); }
::v-deep .el-slider__bar { background-color: #00d2ff; }
::v-deep .el-slider__button { border-color: #00d2ff; background-color: #fff; width: 12px; height: 12px; }
</style>
